/**
 * MobileDialog — canonical modal primitive.
 * Implements Mobile Shell Contract §2.
 *
 *   • max-h: 100dvh-safe-area (.max-h-screen-safe)
 *   • internal scroll (sticky header with X, sticky footer with primary CTA)
 *   • backdrop + ESC + X all dismiss (unless dismissible={false})
 *   • focus restored to trigger on close (via useMobileOverlay)
 *   • body scroll locked while open (via useScrollLock)
 *   • bottom-sheet snap on <sm; centred on ≥sm
 *
 * Two presentation modes:
 *   ── Structured (default) ──
 *     Pass `title`, `description`, `footer`. The primitive renders the
 *     dark-theme sticky header/footer + scrolling body shell.
 *
 *       <MobileDialog open onClose={fn} title="Cleanup" footer={…}>
 *         {body}
 *       </MobileDialog>
 *
 *   ── Bare (bareContent=true) ──
 *     Caller owns the entire panel JSX (colours, padding, rounded
 *     corners). The primitive only enforces the wrapper invariants:
 *     backdrop, ESC, scroll lock, focus restoration, safe-area
 *     bottom, max-h-screen-safe, and the bottom-sheet/centred snap
 *     behaviour. This is the migration target for hand-rolled
 *     modals that already have their own visual styling.
 *
 *       <MobileDialog bareContent open onClose={fn} testid="my-modal">
 *         <div className="bg-white rounded-t-3xl sm:rounded-3xl …">…</div>
 *       </MobileDialog>
 *
 * Phase 1 — Mobile Shell programme. May 14, 2026.
 */
import { X } from "lucide-react";
import { MobileOverlayBackdrop } from "./MobileOverlayBackdrop";

export function MobileDialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  testid = "mobile-dialog",
  widthClass = "sm:max-w-md",
  priority = "default",
  presentation = "auto",
  dismissible = true,
  className = "",
  panelClassName = "",
  bareContent = false,
}) {
  // Centre on >=sm always; on mobile, default presentation is
  // bottom-sheet (rounded-t-3xl), explicit `center` overrides.
  const isCenterOnMobile = presentation === "center";

  // Bare mode: the caller's children ARE the panel. We still wrap
  // in the backdrop primitive so all overlay invariants hold, but
  // we don't impose any visual styling on the inner panel.
  if (bareContent) {
    return (
      <MobileOverlayBackdrop
        open={open}
        onClose={onClose}
        priority={priority}
        dismissible={dismissible}
        testid={`${testid}-backdrop`}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? `${testid}-title` : undefined}
          aria-describedby={description ? `${testid}-desc` : undefined}
          data-testid={testid}
          onClick={(e) => e.stopPropagation()}
          className={[
            "w-full max-h-screen-safe overflow-y-auto overscroll-contain pb-safe-4",
            className,
          ].filter(Boolean).join(" ")}
        >
          {children}
        </div>
      </MobileOverlayBackdrop>
    );
  }

  return (
    <MobileOverlayBackdrop
      open={open}
      onClose={onClose}
      priority={priority}
      dismissible={dismissible}
      testid={`${testid}-backdrop`}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? `${testid}-title` : undefined}
        aria-describedby={description ? `${testid}-desc` : undefined}
        data-testid={testid}
        onClick={(e) => e.stopPropagation()}
        className={[
          "w-full",
          widthClass,
          "max-h-screen-safe overflow-hidden flex flex-col",
          "bg-[#0a0a0a] border border-white/10 text-white shadow-2xl",
          isCenterOnMobile
            ? "rounded-2xl mx-3"
            : "rounded-t-3xl sm:rounded-2xl",
          panelClassName,
          className,
        ].filter(Boolean).join(" ")}
      >
        {/* Sticky header — always shows X (unless dismissible=false) */}
        {(title || dismissible) && (
          <div className="sticky top-0 bg-[#0a0a0a]/95 backdrop-blur z-10 flex items-start gap-3 px-5 pt-5 pb-3 border-b border-white/[0.06]">
            <div className="flex-1 min-w-0">
              {title && (
                <h2
                  id={`${testid}-title`}
                  className="text-base font-bold text-white truncate"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id={`${testid}-desc`}
                  className="text-[12.5px] text-zinc-400 mt-0.5"
                >
                  {description}
                </p>
              )}
            </div>
            {dismissible && (
              <button
                type="button"
                data-testid={`${testid}-close`}
                aria-label="Close"
                onClick={onClose}
                className="flex-shrink-0 w-9 h-9 -mr-2 -mt-1 rounded-full hover:bg-white/[0.06] flex items-center justify-center text-zinc-300 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Body — only this region scrolls */}
        <div
          data-testid={`${testid}-body`}
          className="flex-1 overflow-y-auto overscroll-contain px-5 py-4"
        >
          {children}
        </div>

        {/* Sticky footer with safe-area pad */}
        {footer && (
          <div
            data-testid={`${testid}-footer`}
            className="sticky bottom-0 bg-[#0a0a0a]/95 backdrop-blur border-t border-white/[0.06] px-5 pt-3 pb-safe-4 flex flex-wrap gap-2 justify-end"
          >
            {footer}
          </div>
        )}
      </div>
    </MobileOverlayBackdrop>
  );
}

export default MobileDialog;
